import { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ErrorNotification, PrimaryCardButton } from '@cb/apricot-react';
import { CircularProgress, Grid } from '@mui/material';
import StopCircle from '@mui/icons-material/StopCircle';
import { NewSchoolModal, SchoolSearchForm, SchoolSearchResult, Typography } from 'src/components';
import { useSearchAI } from 'src/apis';
import { useAuth } from 'src/contexts';
import { TSchoolSearchCondition } from 'src/types';
import { IAIUpdateRequest, IOrgAI, isAIActive } from 'src/types';
import { APP_URLS } from 'src/constants';
import { UpdateForm } from 'src/components/school';
import { createAIForm } from 'src/apis';
import { useToasts } from 'src/hooks';


const Search: FC = () => {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [zip, setZip] = useState('');
  const [searched, setSearched] = useState(false);

  const { userName, userEmail } = useAuth();
  const { data, total, isLoading, failure, search } = useSearchAI();

  const [isUpdating, setUpdating] = useState(false);
  const [aiUpdate, setSchoolAI] = useState<Partial<IAIUpdateRequest> | null>(null);
  const [isNewSchoolCodeModalOpen, setIsNewSchoolCodeModalOpen] = useState(false);
  const { render: renderToasts, toastErrorMessage } = useToasts();

  const submitSearch = (params: TSchoolSearchCondition) => {
    setSearched(true);
    setCountry(params.Country);
    setZip(params.Zip);
    setName(params.OrgName);
    search(params);
  };

  const handleClickNewSchoolCodeRequest = useCallback(() => {
    setIsNewSchoolCodeModalOpen(true);
  }, []);

  const handleCloseNewSchoolModal = useCallback(() => {
    setIsNewSchoolCodeModalOpen(false);
  }, []);

  const createUpgradeRequest = async (org: IOrgAI) => {
    const parsed = parseInt(org.aiCategoryCd || "1");
    const aiCategoryCd = !isNaN(parsed) ? parsed : 1;

    const upgrade = {
      schoolName: org.name,
      creator: userName,
      creatorEmail: userEmail,
      processStatus: 'started',
      aiCode: org.aiCode,
      orgId: org.orgId,
      visitingAddress: {
        address1: org.visitingAddress.streetAddr1,
        address2: org.visitingAddress.streetAddr2,
        address3: org.visitingAddress.streetAddr3,
        city: org.visitingAddress.city,
        state: org.visitingAddress.stateCd,
        zip: org.visitingAddress.zip5,
        county: "",
        stateOrProvince: org.visitingAddress.province,
        country: org.visitingAddress.countryIsoCd,
        internationalPostalCode: org.visitingAddress.intlPostalCd
      },
      shippingAddress: org.shippingAddress ? {
        address1: org.shippingAddress.streetAddr1,
        address2: org.shippingAddress.streetAddr2,
        address3: org.shippingAddress.streetAddr3,
        city: org.shippingAddress.city,
        state: org.shippingAddress.stateCd,
        zip: org.shippingAddress.zip5,
        county: "",
        stateOrProvince: org.shippingAddress.province,
        country: org.shippingAddress.countryIsoCd,
        internationalPostalCode: org.shippingAddress.intlPostalCd
      } : undefined,
      phoneNumber: org.phone.formatted || '',
      webSite: org.website,
      schoolEmail: org.email,
      usedName: 'yes',
      usedNameSchool: org.aiCode,
      type: {
        schoolTypePublic: (org.orgSubTypeCd === '15') ? 'yes' : '',
        schoolTypePrivateRel: (org.orgSubTypeCd === '45') ? 'yes' : '',
        schoolTypeCatholic: (org.orgSubTypeCd === '2') ? 'yes' : '',
        schoolTypePrivate: (org.orgSubTypeCd === '11') ? 'yes' : '',
        schoolTypeYouth: '',
        schoolTypeCharter: '',
        schoolTypeHome: (org.orgSubTypeCd === '13' || org.orgSubTypeCd === '12') ? 'yes' : '',
        schoolTypeOnline: '',
        schoolTypeDefense: '',
        schoolTypeIndianAffairs: '',
        schoolTypeOther: (org.orgSubTypeCd === '14') ? 'yes' : ''
      },
      appliedLevel: isAIActive(org.aiStatusCd) ? "2" : org.aiLevelCd,
      appliedCategoryCd: aiCategoryCd,
      level: isAIActive(org.aiStatusCd) ? "2" : org.aiLevelCd
    };

    toastErrorMessage(null);
    try {
      const created = await createAIForm({ ...upgrade, appliedType: 'ai' });
      if (!created.categoryId) throw new Error("No categoryId found on new record.");
      //TODO : Is this correct way to use loading in general? Is it better to set it to false in finally block?
      navigate(APP_URLS.SCHOOL, {
        state: {
          school: { ...upgrade, categoryId: created.categoryId }
        }
      });
    } catch (error: any) {
      console.log('Failed to upgrade school to Level II: ' + error.message);
      toastErrorMessage('Create API Error upgrading school to Level II : ' + error?.message);
    }
  };

  const searchErrorMessage = failure;

  // TO-DO: Setup existing AI interface for data standards
  // will translate the API response into common data interface
  const openUpdateForm = (data: Partial<IAIUpdateRequest>) => {
    setUpdating(true);
    setSchoolAI(data);
  };

  return (
    <Grid container>
      {isUpdating && aiUpdate ?
        <Grid item xs={12}>
          <div style={{ marginBottom: '10px' }}>
            <Button onClick={() => setUpdating(false)}>Close</Button>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <h3>AI Update Request Form</h3>
            <Typography variant="body1" style={{ fontWeight: 700, color: "#ff0000" }}>
              <StopCircle style={{ color: "red", verticalAlign: "middle" }} /> Update requests must be submitted by an authorized user. Authorized users are full-time employees listed in the staff directory on your institution's website.
            </Typography>
          </div>
          <UpdateForm onCancel={() => setUpdating(false)} onComplete={(update) => { setUpdating(false); submitSearch({ OrgName: name, Zip: zip, Country: country }); }} request={aiUpdate} />
        </Grid>
        :
        <Grid item xs={12}>
          <div >
          <Typography variant="h5">College Board School Code Search</Typography>
          </div>
          <br/>
          <div style={{ marginBottom: '20px' }}>
            <SchoolSearchForm init={{ OrgName: name, Zip: zip, Country: country }} onSubmit={submitSearch} />
          </div>
          <NewSchoolModal
            isOpen={isNewSchoolCodeModalOpen}
            onClose={handleCloseNewSchoolModal}
            onComplete={() => { setIsNewSchoolCodeModalOpen(false); navigate(APP_URLS.HOME); }}
          />

          {isLoading && (
            <Grid container item xs={12} justifyContent="center">
              <CircularProgress color="secondary" />
            </Grid>
          )}

          {searchErrorMessage && (
            <Grid item xs={12}>
              {/* @ts-ignore */}
              <ErrorNotification dismissible title="Error">
                {searchErrorMessage}
              </ErrorNotification>
            </Grid>
          )}

          {renderToasts()}

          {total > 0 && (
            <Grid item xs={12} style={{ marginBottom: '10px' }}>
              <Typography align="center" variant="h4">
                {total === 1 && 'Found 1 result.'
                  ? total > 20 && 'Showing the top twenty results.'
                  : 'Found ' + total + ' results.'}
              </Typography>
            </Grid>
          )}

          {!total && !isLoading && searched && (
            <Grid item xs={12}>
              <ErrorNotification dismissible title="There is no school that matches your search criteria." />
              <br></br>
              <PrimaryCardButton className="cb-padding-8" onClick={handleClickNewSchoolCodeRequest}>
                Not Found? Start a new School Code Request.
              </PrimaryCardButton>
            </Grid>
          )}

          {data.length > 0 && (
            <Grid item xs={12}>
              <SchoolSearchResult onUpgrade={(org: IOrgAI) => { createUpgradeRequest(org); }} onOpen={openUpdateForm} schools={data} />
              <br></br>
              <PrimaryCardButton className="cb-padding-8" onClick={handleClickNewSchoolCodeRequest}>
                Not Found? Start a new School Code Request.
              </PrimaryCardButton>
            </Grid>
          )}
        </Grid>
      }
    </Grid>
  );
};

export default Search;
